import { useRef, useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';

import { ApiError, api } from '../api/client';
import type { components } from '../api/schema.gen';
import { useLanguage } from '../lib/i18n/LanguageContext';
import { DateField } from './DateField';
import { formatBalance } from '../lib/money';
import { isValidTaxId, normalizeTaxId } from '../lib/taxId';
import { todayIsoDay } from '../lib/calendar';
import { WHTDocFileError, readWHTDocFile } from '../lib/whtClaimUpload';
import { Modal } from './Modal';

type WHTEligibleTopUp = components['schemas']['WHTEligibleTopUp'];

/**
 * LLMR-235: submit one 50-Tawi certificate against one or more of the
 * caller's own paid top-ups — the "1 document ↔ N top-ups" shape a single
 * claim and a monthly-consolidated one both share (migration 000084's
 * comment). `eligible` is the full checkbox picker's data
 * (GET /topups/wht-eligible); `initialSelectedIds` preselects whatever the
 * caller already checked (a lone row's own "Claim" button preselects just
 * itself) — either way the same checklist inside this modal lets the
 * customer adjust before submitting.
 *
 * Adds no credit itself — only an approved claim (staff review,
 * llmr-admin-web) does that; see this file's onDone contract.
 */
export function WHTClaimModal({
  eligible,
  initialSelectedIds,
  onClose,
  onDone,
}: {
  eligible: WHTEligibleTopUp[];
  initialSelectedIds: Set<string>;
  onClose: () => void;
  onDone: (claimedTopUpIds: string[]) => void;
}) {
  const { dict } = useLanguage();
  const d = dict.whtClaim;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selected, setSelected] = useState<Set<string>>(new Set(initialSelectedIds));
  const [docNumber, setDocNumber] = useState('');
  const [docDate, setDocDate] = useState(() => todayIsoDay());
  const [declaredAmount, setDeclaredAmount] = useState('');
  const [declaredTaxId, setDeclaredTaxId] = useState('');
  const [docFileDataUrl, setDocFileDataUrl] = useState<string | null>(null);
  const [docFileName, setDocFileName] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    totalExpected: string;
    warnings: string[];
  } | null>(null);

  const selectedRows = eligible.filter((t) => selected.has(t.topup_id));
  const computedTotalMicro = selectedRows.reduce((sum, t) => {
    const [whole = '0', frac = '0'] = t.expected_wht.amount.split('.');
    return sum + BigInt(whole) * 1_000_000n + BigInt((frac + '000000').slice(0, 6));
  }, 0n);
  const computedTotal = (computedTotalMicro / 1_000_000n).toString() + '.' +
    (computedTotalMicro % 1_000_000n).toString().padStart(6, '0').slice(0, 2);

  const declaredTaxIdTrimmed = normalizeTaxId(declaredTaxId.trim());
  const declaredTaxIdShowError = declaredTaxIdTrimmed !== '' && !isValidTaxId(declaredTaxIdTrimmed);
  const canSubmit =
    selected.size > 0 &&
    docNumber.trim() !== '' &&
    docDate !== '' &&
    declaredAmount.trim() !== '' &&
    declaredTaxIdTrimmed !== '' &&
    isValidTaxId(declaredTaxIdTrimmed) &&
    docFileDataUrl !== null;

  function toggle(topUpId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(topUpId)) next.delete(topUpId);
      else next.add(topUpId);
      return next;
    });
  }

  async function onPickFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setFileError(null);
    setSubmitError(null);
    try {
      const dataUrl = await readWHTDocFile(file);
      setDocFileDataUrl(dataUrl);
      setDocFileName(file.name);
    } catch (err) {
      if (err instanceof WHTDocFileError) {
        setFileError(err.message === 'too_large' ? d.fileTooLarge : d.fileUnsupportedType);
      } else {
        setFileError(d.fileError);
      }
      setDocFileDataUrl(null);
      setDocFileName(null);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit || !docFileDataUrl || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await api.createWHTClaim({
        docNumber: docNumber.trim(),
        docDate,
        declaredAmount: declaredAmount.trim(),
        declaredTaxId: declaredTaxIdTrimmed,
        docFileDataUrl,
        topUpIds: [...selected],
      });
      setResult({ totalExpected: res.total_expected_wht.amount, warnings: res.validation_warnings });
    } catch (e) {
      setSubmitError(e instanceof ApiError ? (e.problem.detail ?? e.problem.title) : d.submitError);
      setSubmitting(false);
    }
  }

  if (result) {
    return (
      <Modal title={d.successTitle} onClose={() => onDone([...selected])}>
        <p className="note">{d.successBody(formatBalance(result.totalExpected))}</p>
        {result.warnings.length > 0 && (
          <div className="banner banner-warn">
            <p style={{ marginTop: 0 }}>{d.warningsIntro}</p>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {result.warnings.map((w) => (
                <li key={w}>{d.warningLabels[w as keyof typeof d.warningLabels] ?? w}</li>
              ))}
            </ul>
          </div>
        )}
        <div className="modal-actions">
          <button type="button" onClick={() => onDone([...selected])}>
            {dict.common.close}
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={d.modalTitle} onClose={submitting ? undefined : onClose} wide>
      <form onSubmit={(e) => void submit(e)}>
        <p className="note">{d.modalIntro}</p>

        <fieldset className="wht-claim-topup-picker">
          <legend>{d.itemsSelectedLabel(selected.size)}</legend>
          {eligible.map((t) => (
            <label key={t.topup_id} className="wht-claim-topup-row">
              <input type="checkbox" checked={selected.has(t.topup_id)} onChange={() => toggle(t.topup_id)} />
              <span>฿{formatBalance(t.amount.amount)}</span>
              <span className="note">{d.expectedWHTLabel(formatBalance(t.expected_wht.amount))}</span>
            </label>
          ))}
        </fieldset>

        <p className="note wht-claim-computed-total">
          {d.computedTotalLabel(formatBalance(computedTotal))}
        </p>

        <div className="inline-form-field">
          <label htmlFor="wht-doc-number">{d.docNumberLabel}</label>
          <input
            id="wht-doc-number"
            value={docNumber}
            onChange={(e) => setDocNumber(e.target.value)}
            placeholder={d.docNumberPlaceholder}
            required
          />
        </div>

        <div className="inline-form-field">
          <label htmlFor="wht-doc-date">{d.docDateLabel}</label>
          <DateField
            id="wht-doc-date"
            value={docDate}
            max={todayIsoDay()}
            onChange={setDocDate}
            aria-label={d.docDateLabel}
          />
        </div>

        <div className="inline-form-field">
          <label htmlFor="wht-declared-amount">{d.declaredAmountLabel}</label>
          <input
            id="wht-declared-amount"
            inputMode="decimal"
            pattern="[0-9]*[.]?[0-9]*"
            value={declaredAmount}
            onChange={(e) => setDeclaredAmount(e.target.value.replace(/[^\d.]/g, ''))}
            placeholder={d.declaredAmountPlaceholder}
            required
          />
        </div>

        <div className="inline-form-field">
          <label htmlFor="wht-declared-tax-id">{d.declaredTaxIdLabel}</label>
          <input
            id="wht-declared-tax-id"
            inputMode="numeric"
            value={declaredTaxId}
            onChange={(e) => setDeclaredTaxId(e.target.value)}
            placeholder={d.declaredTaxIdPlaceholder}
            required
          />
          {declaredTaxIdShowError && (
            <p className="field-error">{d.declaredTaxIdInvalid}</p>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
          className="visually-hidden"
          onChange={(e) => void onPickFile(e)}
        />
        <div className="inline-form-field">
          <label>{d.fileLabel}</label>
          {docFileName ? (
            <div className="slip-upload-picker">
              <span className="note">{docFileName}</span>
              <button type="button" className="ghost" onClick={() => fileInputRef.current?.click()}>
                {d.chooseAnotherFile}
              </button>
            </div>
          ) : (
            <button type="button" className="ghost btn-icon" onClick={() => fileInputRef.current?.click()}>
              {d.chooseFile}
            </button>
          )}
        </div>
        {fileError && (
          <p className="note warn-text" role="alert">
            {fileError}
          </p>
        )}

        {submitError && (
          <p className="note warn-text" role="alert">
            {submitError}
          </p>
        )}

        <div className="modal-actions">
          <button type="button" className="ghost" onClick={onClose} disabled={submitting}>
            {dict.common.cancel}
          </button>
          <button type="submit" disabled={!canSubmit || submitting}>
            {submitting ? d.submitting : d.submitButton}
          </button>
        </div>
      </form>
    </Modal>
  );
}
